import {
	BackButton,
	LoadingState,
	PageContainer,
	PageHeader,
} from "@client/components/ui";
import { Button } from "@client/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@client/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@client/components/ui/dialog";
import { Input } from "@client/components/ui/input";
import { useAppForm } from "@client/components/ui/tanstack-form";
import { TurnstileWidget } from "@client/components/ui/turnstile";
import { trpc } from "@client/lib/trpc-client";
import { UserProfileUpdateSchema } from "@shared/entities/user";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertTriangle, Camera, Check, Copy, Gift } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/(protected)/settings/account")({
	component: AccountPage,
});

// Define a type for the user profile data
interface UserProfile {
	id?: string;
	name?: string | null;
	email?: string | null;
	image?: string | null;
}

function getInitials(
	name: string | null | undefined,
	email: string | null | undefined,
): string {
	if (name && name.length > 0) {
		return name
			.split(" ")
			.map((part) => part[0])
			.join("")
			.toUpperCase();
	}
	if (email && email.length > 0) {
		return email.split("@")[0].slice(0, 2).toUpperCase();
	}
	return "??";
}

function AccountPage() {
	const navigate = useNavigate();
	const [turnstileToken, setTurnstileToken] = useState<string>("");
	const [dialogOpen, setDialogOpen] = useState(false);
	const turnstileRef = useRef<{
		reset: () => void;
		remove: () => void;
		render: () => void;
	}>(null);

	// Profile functionality
	const [isEditing, setIsEditing] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const profile = trpc.user.getProfile.useQuery();
	const updateMutation = trpc.user.updateProfile.useMutation({
		onSuccess: () => {
			profile.refetch();
			setIsEditing(false);
			toast.success("Profile updated successfully");
		},
		onError: (error) => {
			toast.error(error.message || "Failed to update profile");
		},
	});

	const form = useAppForm({
		defaultValues: {
			name: "",
		},
		validators: {
			onChange: UserProfileUpdateSchema.pick({ name: true }),
		},
		onSubmit: async ({ value }) => {
			if (profile.data) {
				const userData = profile.data as UserProfile;
				updateMutation.mutate({
					name: value.name.trim(),
					image: userData.image ?? undefined,
				});
			}
		},
	});

	const handleSubmit = useCallback(
		(e: React.FormEvent) => {
			e.preventDefault();
			e.stopPropagation();
			void form.handleSubmit();
		},
		[form],
	);

	useEffect(() => {
		if (profile.data) {
			const userData = profile.data as UserProfile;
			form.setFieldValue("name", userData.name ?? "New User");
		}
	}, [profile.data, form]);

	const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;

		if (!file.type.startsWith("image/")) {
			toast.error("Please select an image file");
			return;
		}

		const img = new Image();
		img.src = URL.createObjectURL(file);
		img.onload = () => {
			if (img.width > 512 || img.height > 512) {
				toast.error("Image must be 512x512 pixels or smaller");
				return;
			}

			const reader = new FileReader();
			reader.onload = async (e) => {
				const base64Image = e.target?.result as string;
				if (!profile.data) return;

				const userData = profile.data as UserProfile;
				updateMutation.mutate({
					name: userData.name ?? "New User",
					image: base64Image,
				});
			};
			reader.readAsDataURL(file);
		};
	};

	const deleteAccountMutation = trpc.user.deleteAccount.useMutation({
		onSuccess: () => {
			toast.success("Account deleted successfully");
			navigate({ to: "/" });
		},
		onError: (error) => {
			toast.error(error.message || "Failed to delete account");
			resetTurnstile();
		},
	});

	const handleTurnstileVerify = (token: string) => {
		setTurnstileToken(token);
	};

	const handleTurnstileError = () => {
		setTurnstileToken("");
		toast.error("Verification failed. Please try again.");
	};

	const handleTurnstileExpire = () => {
		setTurnstileToken("");
		toast.warning("Verification expired. Please complete it again.");
	};

	const resetTurnstile = () => {
		if (turnstileRef.current) {
			turnstileRef.current.reset();
		}
		setTurnstileToken("");
	};

	const handleDeleteAccount = () => {
		if (!turnstileToken) {
			toast.error("Please complete the verification challenge first.");
			return;
		}

		deleteAccountMutation.mutate({
			turnstileToken,
		});
	};

	return (
		<PageContainer maxWidth="narrow">
			<BackButton to="/settings" label="Back to Settings" />

			<PageHeader
				title="Account Settings"
				description="Manage your profile and account settings"
			/>

			<div className="space-y-6">
				{/* Profile Information Card */}
				<Card className="border-2 dark:border-gray-700">
					<CardHeader>
						<CardTitle className="dark:text-gray-100">
							Profile Information
						</CardTitle>
						<CardDescription className="dark:text-gray-400">
							Update your profile picture and display name
						</CardDescription>
					</CardHeader>
					<CardContent>
						{profile.isLoading ? (
							<LoadingState variant="inline" message="Loading profile..." />
						) : !profile.data ? (
							<div className="rounded-lg border border-red-200 bg-red-50 p-4 text-center dark:border-red-900 dark:bg-red-950/30">
								<p className="text-red-900 text-sm dark:text-red-200">
									Profile not available
								</p>
							</div>
						) : (
							<div className="px-4 py-3">
								{(() => {
									const userData = profile.data as UserProfile;
									return (
										<div className="flex flex-col items-center sm:flex-row sm:items-start sm:space-x-4">
											<div className="group relative">
												<div
													className={`relative h-20 w-20 overflow-hidden rounded-full bg-muted dark:bg-gray-800 ${isEditing ? "ring-2 ring-primary ring-offset-2 dark:ring-offset-gray-900" : ""}`}
												>
													{userData.image ? (
														<img
															src={userData.image}
															alt={userData.name ?? "User"}
															className="h-full w-full rounded-full object-cover"
														/>
													) : (
														<div className="flex h-full w-full items-center justify-center rounded-full bg-primary font-bold text-3xl text-primary-foreground">
															{getInitials(userData.name, userData.email)}
														</div>
													)}
													<Button
														variant="ghost"
														size="icon"
														className={`absolute inset-0 flex h-full w-full items-center justify-center bg-black/50 transition-opacity ${isEditing ? "opacity-100 hover:opacity-80" : "opacity-0 group-hover:opacity-100"}`}
														onClick={() => fileInputRef.current?.click()}
													>
														{isEditing ? (
															<Camera className="h-5 w-5 text-white" />
														) : (
															<span className="text-sm text-white">Change</span>
														)}
													</Button>
												</div>
												<input
													ref={fileInputRef}
													type="file"
													accept="image/*"
													className="hidden"
													onChange={handleFileChange}
												/>
											</div>
											<div className="mt-4 w-full flex-1 text-center sm:mt-0 sm:text-left">
												{isEditing ? (
													<form.AppForm>
														<form className="space-y-3" onSubmit={handleSubmit}>
															<form.AppField name="name">
																{(field) => (
																	<field.FormItem>
																		<field.FormLabel>
																			Display Name
																		</field.FormLabel>
																		<field.FormControl>
																			<div className="relative">
																				<Input
																					value={field.state.value}
																					onChange={(e) =>
																						field.handleChange(e.target.value)
																					}
																					onBlur={field.handleBlur}
																					className="text-lg"
																					maxLength={20}
																					disabled={updateMutation.isPending}
																				/>
																				<span className="-translate-y-1/2 absolute top-1/2 right-2 text-muted-foreground text-sm">
																					{field.state.value.length}/20
																				</span>
																			</div>
																		</field.FormControl>
																		<field.FormMessage />
																	</field.FormItem>
																)}
															</form.AppField>
															<div className="flex space-x-2">
																<form.Subscribe selector={(state) => state}>
																	{(state) => (
																		<Button
																			type="submit"
																			disabled={
																				!state.canSubmit ||
																				state.isSubmitting ||
																				updateMutation.isPending
																			}
																		>
																			{updateMutation.isPending
																				? "Saving..."
																				: "Save"}
																		</Button>
																	)}
																</form.Subscribe>
																<Button
																	variant="outline"
																	onClick={() => {
																		setIsEditing(false);
																		form.setFieldValue(
																			"name",
																			userData.name ?? "New User",
																		);
																	}}
																>
																	Cancel
																</Button>
															</div>
														</form>
													</form.AppForm>
												) : (
													<div className="space-y-0.5">
														<div className="flex items-start justify-between">
															<h3 className="font-semibold text-2xl dark:text-gray-100">
																{userData.name ?? "New User"}
															</h3>
															<Button
																variant="outline"
																size="sm"
																onClick={() => setIsEditing(true)}
															>
																Edit Profile
															</Button>
														</div>
														<p className="text-muted-foreground dark:text-gray-400">
															{userData.email}
														</p>
													</div>
												)}
											</div>
										</div>
									);
								})()}
							</div>
						)}
					</CardContent>
				</Card>

				{/* Authentication Card */}
				<Card className="border-2 dark:border-gray-700">
					<CardHeader>
						<CardTitle className="dark:text-gray-100">
							Connected Accounts
						</CardTitle>
						<CardDescription className="dark:text-gray-400">
							Add additional authentication methods to your account
						</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						<div className="grid gap-2">
							<Button
								variant="outline"
								className="justify-start text-left dark:hover:bg-gray-800"
							>
								<svg
									className="mr-2 h-4 w-4"
									xmlns="http://www.w3.org/2000/svg"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
									aria-hidden="true"
								>
									<title>Google Icon</title>
									<path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
									<path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
									<path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
									<path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
								</svg>
								Connect Google
							</Button>
							<Button
								variant="outline"
								className="justify-start text-left dark:hover:bg-gray-800"
							>
								<svg
									className="mr-2 h-4 w-4"
									xmlns="http://www.w3.org/2000/svg"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
									aria-hidden="true"
								>
									<title>GitHub Icon</title>
									<path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
								</svg>
								Connect GitHub
							</Button>
						</div>
					</CardContent>
				</Card>

				{/* Refer a Friend Card */}
				<Card className="border-2 border-primary/20 dark:border-primary/30">
					<CardHeader>
						<CardTitle className="flex items-center gap-2 dark:text-gray-100">
							<Gift className="h-5 w-5 text-primary" />
							Refer a Friend
						</CardTitle>
						<CardDescription className="dark:text-gray-400">
							Share your link and you both get 100 free credits when they sign
							up
						</CardDescription>
					</CardHeader>
					<CardContent>
						{profile.isLoading ? (
							<LoadingState variant="inline" message="Loading..." />
						) : (
							(() => {
								const userData = profile.data as UserProfile & {
									referralCode?: string;
								};
								const referralCode = userData?.referralCode;
								const baseUrl = import.meta.env.PROD
									? "https://app.llmgenerator.com"
									: import.meta.env.VITE_FRONTEND_URL ||
										"http://localhost:5173";
								const referralLink = referralCode
									? `${baseUrl}/?ref=${referralCode}`
									: null;

								if (!referralLink) {
									return (
										<p className="text-muted-foreground text-sm">
											Your referral link is being generated...
										</p>
									);
								}

								return <ReferralLinkCopy link={referralLink} />;
							})()
						)}
					</CardContent>
				</Card>

				{/* Danger Zone Card */}
				<Card className="border-2 border-destructive/20 dark:border-red-900/50">
					<CardHeader>
						<CardTitle className="text-destructive dark:text-red-400">
							Danger Zone
						</CardTitle>
						<CardDescription className="dark:text-gray-400">
							Permanently delete your account and all associated data
						</CardDescription>
					</CardHeader>
					<CardContent>
						<Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
							<DialogTrigger asChild>
								<Button variant="destructive">Delete Account</Button>
							</DialogTrigger>
							<DialogContent className="dark:border-gray-700">
								<DialogHeader>
									<DialogTitle className="flex items-center dark:text-gray-100">
										<AlertTriangle className="mr-2 h-5 w-5 text-red-500 dark:text-red-400" />
										Delete Account
									</DialogTitle>
									<DialogDescription className="dark:text-gray-400">
										This action cannot be undone. This will permanently delete
										your account and remove all your data from our servers.
									</DialogDescription>
								</DialogHeader>
								<div className="space-y-4 py-4">
									<div className="space-y-2">
										<div className="flex justify-start pt-2">
											<TurnstileWidget
												ref={turnstileRef}
												onVerify={handleTurnstileVerify}
												onError={handleTurnstileError}
												onExpire={handleTurnstileExpire}
											/>
										</div>
									</div>
								</div>
								<DialogFooter>
									<Button
										variant="outline"
										onClick={() => {
											setDialogOpen(false);
											resetTurnstile();
										}}
										disabled={deleteAccountMutation.isPending}
									>
										Cancel
									</Button>
									<Button
										variant="destructive"
										onClick={handleDeleteAccount}
										disabled={
											!turnstileToken || deleteAccountMutation.isPending
										}
									>
										{deleteAccountMutation.isPending
											? "Deleting..."
											: "Delete Account"}
									</Button>
								</DialogFooter>
							</DialogContent>
						</Dialog>
					</CardContent>
				</Card>
			</div>
		</PageContainer>
	);
}

function ReferralLinkCopy({ link }: { link: string }) {
	const [copied, setCopied] = useState(false);

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(link);
			setCopied(true);
			toast.success("Referral link copied!");
			setTimeout(() => setCopied(false), 2000);
		} catch {
			toast.error("Failed to copy link");
		}
	};

	return (
		<div className="space-y-3">
			<div className="flex items-center gap-2">
				<Input
					readOnly
					value={link}
					className="flex-1 bg-muted/50 font-mono text-sm dark:bg-gray-800"
					onClick={(e) => (e.target as HTMLInputElement).select()}
				/>
				<Button
					variant="outline"
					size="icon"
					onClick={handleCopy}
					className="shrink-0"
				>
					{copied ? (
						<Check className="h-4 w-4 text-green-500" />
					) : (
						<Copy className="h-4 w-4" />
					)}
				</Button>
			</div>
			<p className="text-muted-foreground text-sm">
				When someone signs up using your link, you'll both receive{" "}
				<span className="font-semibold text-primary">100 free credits</span>.
			</p>
		</div>
	);
}
